import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Pencil, Trash2, Loader2, X, Upload, Image, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCategories } from "@/hooks/useCategories";

interface ProductUsages {
  winter: number;
  spring: number;
  summer: number;
  fall: number;
  day: number;
  night: number;
}

interface Product {
  id: string;
  name: string;
  name_arabic: string | null;
  price: number;
  category: string;
  categories: string[];
  image_url: string | null;
  images: string[];
  story: string | null;
  in_stock: boolean;
  is_best_seller: boolean;
  sizes: { label: string; price: number }[];
  notes: { top: string[]; middle: string[]; base: string[] };
  ingredients: string[];
  usages: ProductUsages;
}

const defaultUsages: ProductUsages = {
  winter: 0,
  spring: 0,
  summer: 0,
  fall: 0,
  day: 0,
  night: 0,
};

const emptyProduct: Omit<Product, "id"> = {
  name: "",
  name_arabic: "",
  price: 0,
  category: "",
  categories: [],
  image_url: "",
  images: [],
  story: "",
  in_stock: true,
  is_best_seller: false,
  sizes: [{ label: "50ml EDP", price: 0 }],
  notes: { top: [], middle: [], base: [] },
  ingredients: [],
  usages: { ...defaultUsages },
};

const AdminProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Partial<Product> | null>(null);
  const [formData, setFormData] = useState<Omit<Product, "id">>(emptyProduct);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { data: categories = [] } = useCategories(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const transformedProducts = (data || []).map((p) => ({
        id: p.id,
        name: p.name,
        name_arabic: p.name_arabic,
        price: p.price,
        category: p.category,
        categories: (p as any).categories || [p.category],
        image_url: p.image_url,
        images: p.images || [],
        story: p.story,
        in_stock: p.in_stock ?? true,
        is_best_seller: (p as any).is_best_seller ?? false,
        sizes: (Array.isArray(p.sizes) ? p.sizes : []).map((s: unknown) => {
          const size = s as { label?: string; price?: number };
          return { label: size.label || "", price: size.price || 0 };
        }),
        notes: p.notes && typeof p.notes === 'object' && !Array.isArray(p.notes)
          ? p.notes as { top: string[]; middle: string[]; base: string[] }
          : { top: [], middle: [], base: [] },
        ingredients: p.ingredients || [],
        usages: p.usages && typeof p.usages === 'object' && !Array.isArray(p.usages)
          ? { ...defaultUsages, ...(p.usages as unknown as ProductUsages) }
          : { ...defaultUsages },
      }));

      setProducts(transformedProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        name_arabic: product.name_arabic || "",
        price: product.price,
        category: product.category,
        categories: product.categories || [product.category],
        image_url: product.image_url || "",
        images: product.images || [],
        story: product.story || "",
        in_stock: product.in_stock,
        is_best_seller: product.is_best_seller,
        sizes: product.sizes || [{ label: "50ml EDP", price: 0 }],
        notes: product.notes || { top: [], middle: [], base: [] },
        ingredients: product.ingredients || [],
        usages: product.usages || { ...defaultUsages },
      });
    } else {
      setEditingProduct(null);
      setFormData(emptyProduct);
    }
    setIsDialogOpen(true);
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `products/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("product-images")
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase.storage
      .from("product-images")
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Validate all files
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) {
        toast({
          title: "Invalid file",
          description: "Please upload only image files",
          variant: "destructive",
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Each image must be less than 5MB",
          variant: "destructive",
        });
        return;
      }
    }

    setUploading(true);

    try {
      const uploadedUrls: string[] = [];

      for (const file of Array.from(files)) {
        const url = await uploadImage(file);
        if (url) uploadedUrls.push(url);
      }

      const newImages = [...formData.images, ...uploadedUrls];
      const mainImage = formData.image_url || newImages[0] || "";

      setFormData({
        ...formData,
        images: newImages,
        image_url: mainImage,
      });

      toast({
        title: "Images uploaded",
        description: `${uploadedUrls.length} image(s) uploaded successfully`,
      });
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload images",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    const removedWasMain = formData.image_url === formData.images[index];
    
    setFormData({
      ...formData,
      images: newImages,
      image_url: removedWasMain ? (newImages[0] || "") : formData.image_url,
    });
  };

  const setMainImage = (url: string) => {
    setFormData({ ...formData, image_url: url });
  };

  const handleSave = async () => {
    if (!formData.name || formData.categories.length === 0 || formData.price <= 0) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);

    try {
      const productData = {
        name: formData.name,
        name_arabic: formData.name_arabic || null,
        price: formData.price,
        category: formData.categories[0] || formData.category,
        categories: formData.categories,
        image_url: formData.image_url || formData.images[0] || null,
        images: formData.images,
        story: formData.story || null,
        in_stock: formData.in_stock,
        is_best_seller: formData.is_best_seller,
        sizes: JSON.parse(JSON.stringify(formData.sizes)),
        notes: JSON.parse(JSON.stringify(formData.notes)),
        ingredients: formData.ingredients,
        usages: JSON.parse(JSON.stringify(formData.usages)),
      };

      if (editingProduct?.id) {
        const { error } = await supabase
          .from("products")
          .update(productData)
          .eq("id", editingProduct.id);

        if (error) throw error;
        toast({ title: "Success", description: "Product updated successfully" });
      } else {
        const { error } = await supabase.from("products").insert(productData);

        if (error) throw error;
        toast({ title: "Success", description: "Product created successfully" });
      }

      setIsDialogOpen(false);
      fetchProducts();
    } catch (error: any) {
      console.error("Error saving product:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save product",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;

    try {
      const { error } = await supabase.from("products").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "Product deleted successfully" });
      fetchProducts();
    } catch (error: any) {
      console.error("Error deleting product:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete product",
        variant: "destructive",
      });
    }
  };

  const addSize = () => {
    setFormData({
      ...formData,
      sizes: [...formData.sizes, { label: "", price: 0 }],
    });
  };

  const updateSize = (index: number, field: "label" | "price", value: string | number) => {
    const newSizes = [...formData.sizes];
    newSizes[index] = { ...newSizes[index], [field]: value };
    setFormData({ ...formData, sizes: newSizes });
  };

  const removeSize = (index: number) => {
    setFormData({
      ...formData,
      sizes: formData.sizes.filter((_, i) => i !== index),
    });
  };

  // Notes management
  const addNote = (type: "top" | "middle" | "base") => {
    setFormData({
      ...formData,
      notes: {
        ...formData.notes,
        [type]: [...formData.notes[type], ""],
      },
    });
  };

  const updateNote = (type: "top" | "middle" | "base", index: number, value: string) => {
    const newNotes = { ...formData.notes };
    newNotes[type] = [...newNotes[type]];
    newNotes[type][index] = value;
    setFormData({ ...formData, notes: newNotes });
  };

  const removeNote = (type: "top" | "middle" | "base", index: number) => {
    setFormData({
      ...formData,
      notes: {
        ...formData.notes,
        [type]: formData.notes[type].filter((_, i) => i !== index),
      },
    });
  };

  // Ingredients management
  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, ""],
    });
  };

  const updateIngredient = (index: number, value: string) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = value;
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const removeIngredient = (index: number) => {
    setFormData({
      ...formData,
      ingredients: formData.ingredients.filter((_, i) => i !== index),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-serif">Products</h1>
        <Button variant="elegant" onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        <AnimatePresence>
          {products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="bg-card border border-border rounded-sm p-4"
            >
              <div className="flex gap-3">
                {product.image_url && (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-16 h-16 object-cover rounded-sm flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.name}</p>
                  {product.name_arabic && (
                    <p className="text-sm text-muted-foreground arabic truncate">{product.name_arabic}</p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-muted-foreground">{product.categories?.join(", ") || product.category}</span>
                    <span className="text-sm font-medium">${product.price}</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full ${
                      product.in_stock
                        ? "bg-green-500/10 text-green-500"
                        : "bg-red-500/10 text-red-500"
                    }`}
                  >
                    {product.in_stock ? "In Stock" : "Out of Stock"}
                  </span>
                  {product.is_best_seller && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-primary/10 text-primary flex items-center gap-1">
                      <Star className="w-3 h-3 fill-current" /> Best Seller
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {product.images?.length || 0} images
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDialog(product)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(product.id)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {products.length === 0 && (
          <div className="p-8 text-center text-muted-foreground bg-card border border-border rounded-sm">
            No products yet. Click "Add Product" to create your first product.
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Product</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Category</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Price</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Images</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Featured</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {products.map((product) => (
                  <motion.tr
                    key={product.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        {product.image_url && (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-sm"
                          />
                        )}
                        <div>
                          <p className="font-medium">{product.name}</p>
                          {product.name_arabic && (
                            <p className="text-sm text-muted-foreground arabic">{product.name_arabic}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-muted-foreground">{product.categories?.join(", ") || product.category}</td>
                    <td className="px-4 py-4">${product.price}</td>
                    <td className="px-4 py-4 text-muted-foreground">{product.images?.length || 0}</td>
                    <td className="px-4 py-4">
                      <span
                        className={`px-3 py-1 text-xs rounded-full ${
                          product.in_stock
                            ? "bg-green-500/10 text-green-500"
                            : "bg-red-500/10 text-red-500"
                        }`}
                      >
                        {product.in_stock ? "In Stock" : "Out of Stock"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {product.is_best_seller ? (
                        <span className="px-3 py-1 text-xs rounded-full bg-primary/10 text-primary flex items-center gap-1 w-fit">
                          <Star className="w-3 h-3 fill-current" /> Best Seller
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(product)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(product.id)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>

          {products.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No products yet. Click "Add Product" to create your first product.
            </div>
          )}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingProduct ? "Edit Product" : "Add New Product"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Name *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Product name"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Arabic Name</label>
                <Input
                  value={formData.name_arabic || ""}
                  onChange={(e) => setFormData({ ...formData, name_arabic: e.target.value })}
                  placeholder="الاسم بالعربي"
                  className="arabic"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Categories * <span className="text-xs text-muted-foreground">(select one or more)</span></label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-border rounded-sm p-3">
                  {categories.map((cat) => {
                    const isSelected = formData.categories.includes(cat.name);
                    return (
                      <label key={cat.id} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded cursor-pointer transition-colors ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const newCategories = e.target.checked
                              ? [...formData.categories, cat.name]
                              : formData.categories.filter(c => c !== cat.name);
                            setFormData({ ...formData, categories: newCategories, category: newCategories[0] || "" });
                          }}
                          className="rounded border-border"
                        />
                        {cat.name} {cat.name_arabic ? `(${cat.name_arabic})` : ""}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Base Price *</label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={formData.price || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    setFormData({ ...formData, price: Number(value) || 0 });
                  }}
                  placeholder="0.00"
                  className="[appearance:textfield]"
                />
              </div>
            </div>

            {/* Multi-Image Upload */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Product Images</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Add Images
                </Button>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              {formData.images.length > 0 ? (
                <div className="grid grid-cols-4 gap-3">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`Product ${index + 1}`}
                        className={`w-full aspect-square object-cover rounded-sm border-2 ${
                          formData.image_url === image ? "border-primary" : "border-border"
                        }`}
                      />
                      {formData.image_url === image && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-primary text-primary-foreground text-xs rounded">
                          Main
                        </span>
                      )}
                      <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {formData.image_url !== image && (
                          <button
                            type="button"
                            onClick={() => setMainImage(image)}
                            className="p-1.5 bg-primary text-primary-foreground rounded-sm text-xs"
                          >
                            <Image className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="p-1.5 bg-destructive text-destructive-foreground rounded-sm"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full h-32 border-2 border-dashed border-border rounded-sm flex flex-col items-center justify-center gap-2 hover:border-primary hover:bg-muted/20 transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Click to upload images</span>
                    </>
                  )}
                </button>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Upload multiple images. First image will be the main image by default.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Story</label>
              <Textarea
                value={formData.story || ""}
                onChange={(e) => setFormData({ ...formData, story: e.target.value })}
                placeholder="The story behind this fragrance..."
                rows={3}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Sizes & Prices</label>
                <Button type="button" variant="outline" size="sm" onClick={addSize}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Size
                </Button>
              </div>
              <div className="space-y-2">
                {formData.sizes.map((size, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={size.label}
                      onChange={(e) => updateSize(index, "label", e.target.value)}
                      placeholder="e.g., 50ml EDP"
                      className="flex-1"
                    />
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={size.price || ""}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, "");
                        updateSize(index, "price", Number(value) || 0);
                      }}
                      placeholder="Price"
                      className="w-28 [appearance:textfield]"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeSize(index)}
                      className="text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* Fragrance Notes */}
            <div className="space-y-4">
              <label className="text-sm font-medium block">Fragrance Notes</label>
              
              {/* Top Notes */}
              <div className="border border-border rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Top Notes</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addNote("top")}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.notes.top.map((note, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={note}
                        onChange={(e) => updateNote("top", index, e.target.value)}
                        placeholder="e.g., Bergamot"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeNote("top", index)}
                        className="text-destructive h-10 w-10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {formData.notes.top.length === 0 && (
                    <p className="text-xs text-muted-foreground">No top notes added</p>
                  )}
                </div>
              </div>

              {/* Middle Notes */}
              <div className="border border-border rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Middle Notes</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addNote("middle")}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.notes.middle.map((note, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={note}
                        onChange={(e) => updateNote("middle", index, e.target.value)}
                        placeholder="e.g., Rose"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeNote("middle", index)}
                        className="text-destructive h-10 w-10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {formData.notes.middle.length === 0 && (
                    <p className="text-xs text-muted-foreground">No middle notes added</p>
                  )}
                </div>
              </div>

              {/* Base Notes */}
              <div className="border border-border rounded-sm p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Base Notes</span>
                  <Button type="button" variant="ghost" size="sm" onClick={() => addNote("base")}>
                    <Plus className="w-3 h-3 mr-1" />
                    Add
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.notes.base.map((note, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={note}
                        onChange={(e) => updateNote("base", index, e.target.value)}
                        placeholder="e.g., Oud"
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeNote("base", index)}
                        className="text-destructive h-10 w-10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {formData.notes.base.length === 0 && (
                    <p className="text-xs text-muted-foreground">No base notes added</p>
                  )}
                </div>
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Ingredients</label>
                <Button type="button" variant="outline" size="sm" onClick={addIngredient}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Ingredient
                </Button>
              </div>
              <div className="space-y-2">
                {formData.ingredients.map((ingredient, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={ingredient}
                      onChange={(e) => updateIngredient(index, e.target.value)}
                      placeholder="e.g., Natural Oud Extract"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeIngredient(index)}
                      className="text-destructive"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {formData.ingredients.length === 0 && (
                  <p className="text-xs text-muted-foreground">No ingredients added</p>
                )}
              </div>
            </div>

            {/* Seasonal Usages */}
            <div className="space-y-3">
              <label className="text-sm font-medium block">Seasonal Usages (%)</label>
              <p className="text-xs text-muted-foreground">Set the percentage for when this fragrance is best worn</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="border border-border rounded-sm p-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                    <span>❄️</span> Winter
                  </label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.usages.winter || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setFormData({ ...formData, usages: { ...formData.usages, winter: Number(value) || 0 } });
                    }}
                    placeholder="0"
                    className="[appearance:textfield]"
                  />
                </div>
                <div className="border border-border rounded-sm p-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                    <span>🌸</span> Spring
                  </label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.usages.spring || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setFormData({ ...formData, usages: { ...formData.usages, spring: Number(value) || 0 } });
                    }}
                    placeholder="0"
                    className="[appearance:textfield]"
                  />
                </div>
                <div className="border border-border rounded-sm p-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                    <span>☀️</span> Summer
                  </label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.usages.summer || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setFormData({ ...formData, usages: { ...formData.usages, summer: Number(value) || 0 } });
                    }}
                    placeholder="0"
                    className="[appearance:textfield]"
                  />
                </div>
                <div className="border border-border rounded-sm p-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                    <span>🍂</span> Fall
                  </label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.usages.fall || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setFormData({ ...formData, usages: { ...formData.usages, fall: Number(value) || 0 } });
                    }}
                    placeholder="0"
                    className="[appearance:textfield]"
                  />
                </div>
                <div className="border border-border rounded-sm p-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                    <span>🌤️</span> Day
                  </label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.usages.day || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setFormData({ ...formData, usages: { ...formData.usages, day: Number(value) || 0 } });
                    }}
                    placeholder="0"
                    className="[appearance:textfield]"
                  />
                </div>
                <div className="border border-border rounded-sm p-3">
                  <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-2 mb-2">
                    <span>🌙</span> Night
                  </label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={formData.usages.night || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, "");
                      setFormData({ ...formData, usages: { ...formData.usages, night: Number(value) || 0 } });
                    }}
                    placeholder="0"
                    className="[appearance:textfield]"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="in_stock"
                  checked={formData.in_stock}
                  onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                  className="rounded border-border"
                />
                <label htmlFor="in_stock" className="text-sm">In Stock</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_best_seller"
                  checked={formData.is_best_seller}
                  onChange={(e) => setFormData({ ...formData, is_best_seller: e.target.checked })}
                  className="rounded border-border"
                />
                <label htmlFor="is_best_seller" className="text-sm flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-primary" />
                  Best Seller (appears on homepage)
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="elegant" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Product"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
